/**
 * Part of main Application Store.
 * Response for prepare Dogtag information.
 */
import { ref } from 'vue';
import { defineStore } from 'pinia';
import { associateBy } from '@std/collections';

import type { Clan } from '@/types/vortex';
import type { Dogtag } from '@/types/gloss';
import {
  requestAccountComponentsIdByAccountId,
  requestClanInfo,
  type GlossaryResponse,
} from '@/composables/network-manager';

import { defaultDogtag } from '@/components/dogtag/model';

import type { DogTagComponent, DogtagComponentMap, DogtagStore as DogtagStoreState } from './types';

const isInit = ref(false);
const ID = 'mk-replay-dogtag-store';

const TTL = 14_400_000; // 4 hours

export const useDogtagStore = defineStore(ID, () => {
  const store = ref<DogtagStoreState>({
    account: {},
    clan: {},
  });

  const fillData = (data: GlossaryResponse | null) => {
    isInit.value = true;
    store.value.component = associateBy(
      data?.data.dogTagComponents as DogTagComponent[],
      (i) => i.id!
    );
    const storageData = loadFromLocalStorage();

    const now = new Date().getTime();
    const isExpired = now - Number(storageData?.ttl ?? 0) > TTL;
    if (!isExpired) {
      store.value.clan = storageData?.clan ?? {};
      store.value.account = storageData?.account ?? {};
    } else {
      clearStorage();
    }
  };

  const getDogtagComponentsMap = async (
    accountId: number
  ): Promise<DogtagComponentMap | undefined> => {
    let result: DogtagComponentMap | undefined;

    if (store.value.account && accountId in store.value.account) {
      result = store.value.account[accountId];
    }

    if (!result) {
      let componentsIdObject: Dogtag | undefined;
      try {
        componentsIdObject = await requestAccountComponentsIdByAccountId(accountId);
        if (!componentsIdObject) {
          componentsIdObject = defaultDogtag;
        }
      } catch (e) {
        console.error('cant get Dogtag components from account id', e);
        componentsIdObject = defaultDogtag;
      }

      const componentIdList = Object.values(componentsIdObject);
      const componentList = componentIdList
        .map((id) => (id in store.value.component! ? store.value.component![id] : undefined))
        .filter((i) => i !== undefined);

      result = associateBy<DogTagComponent>(componentList, (component) => component.type);
    }

    if (result) {
      store.value.account[accountId] = result;
    }

    saveToLocalStorage();

    return result;
  };

  const getClanDataByAccountId = async (accountId: number): Promise<Clan | undefined> => {
    let result: Clan | undefined;

    if (store.value.clan && accountId in store.value.clan) {
      result = store.value.clan[accountId];
    }

    if (!result) {
      result = await requestClanInfo(accountId);
    }

    // add result info to store
    if (result) {
      store.value.clan[accountId] = result;
    }

    saveToLocalStorage();

    return result;
  };

  const clearStorage = () => {
    console.log('clear storage');
    localStorage.removeItem(ID);
  };

  const saveToLocalStorage = () => {
    try {
      const preparedData: DogtagStoreState = {
        account: store.value.account,
        clan: store.value.clan,
        ttl: new Date().getTime(),
      };

      localStorage.setItem(ID, JSON.stringify(preparedData));
    } catch (e) {
      console.error('cannot save local storage', e);
    }
  };

  const loadFromLocalStorage = () => {
    let result: DogtagStoreState | undefined;
    try {
      const stringData = localStorage.getItem(ID);
      if (stringData) result = JSON.parse(stringData) as DogtagStoreState;
    } catch (e) {
      console.error('cannot load from local storage', e);
      return result;
    }

    return result;
  };

  return {
    state: store,
    fillData,
    isInit,
    getDogtagComponentsMap,
    getClanDataByAccountId,
  };
});

export type DogtagStore = ReturnType<typeof useDogtagStore>;
